/**
 * @module lib/commercial/keytar/Wall
 */
import tgamConfig from "config/tgam";
import logger from "utils/logger";
import { getProp } from "utils/utils";
import WallAnalyticsSchema from "./WallAnalyticsSchema";

const debugTag = "[WALL]";

/**
 * Create a basic Keytar wall (inherited)
 * @class Wall
 */
export default class Wall {
  /**
   * Creates an instance of Wall.
   * @param {String} name - of the wall (unique per wall)
   * @param {String} type - of the wall (for analytics reporting)
   * @param {Object} [props={}] - of secondary property values
   * @param {Array<Object>} [props.rules] - various rules used to determine if wall is active
   * @param {Function} [props.suppressed] - optional custom logic that returns a Boolean indicating whether the wall should be suppressed
   * @param {String} [props.featureFlagName] - used to determine if wall is enabled via PB runtime property
   * @param {Object} [props.template] - that renders the Wall's UI markup
   * @param {Object} [config=tgamConfig] - equivalent of tgamConfig
   */
  constructor(name, type, props = {}, config = tgamConfig) {
    if (!name || !type) {
      throw new Error("Wall class constructor requires a name and a type");
    }
    this.name = name;
    this.type = type.toLowerCase();
    this.customer = null; // set in wallIsActive()
    this.matchedRule = null; // set in wallIsActive()
    this.tgam = config;

    if (props.rules) {
      this.rules = props.rules;
    }
    if (props.featureFlagName) {
      this.featureFlagName = props.featureFlagName;
    }
    if (props.template) {
      this.template = props.template;
      this.template.parentContext = this;
    }

    this.wallAnalyticsSchema = new WallAnalyticsSchema({
      name: this.name.toLowerCase(),
      type: this.type.toLowerCase()
    });

    this._globalEnabled = (getProp(this.tgam, "keytar.enabled") === "true")
      || (getProp(this.tgam, "env.isPbAdmin") === true);

    this._instanceEnabled = getProp(this.tgam, `keytar.${this.featureFlagName}`) === "true";

    this._disabledUrlPatterns = [
      /^https:\/\/([\w+\d+\-\.])+\.tgam\.arcpublishing\.com\/(pb|ellipsis|composer|pf)?/im
    ];

    this.suppressed = props.suppressed || (() => false);

    this._enabled = this.shouldRun();
  }

  /**
   * Determine if access is allowed to page for user
   * @readonly
   * @returns {Boolean} true when permitted
   */
  get accessAllowed() {
    return this.matchedRule ? // is null when Hermes takes over
      this.matchedRule.accessAllowed({
        customer: this.customer,
        gam: this.tgam
      }) : false;
  }

  /**
   * Dynamically add a specific service for customer.
   * @param {String} service - to add
   */
  enableAccess(service) {
    this.customer.addService(service);
  }

  /**
   * Process a pageview and determine if the user has access.
   * @param {Boolean} [forceRender=false] force render the wall.
   * @param {Object} [analyticsData={}] - additional analytics data (e.g. Hermes analytics)
   * @returns {Promise<Boolean>} true if user can bypass the wall.
   */
  pageview(forceRender = false, analyticsData = {}) {
    // If hermes takes over, don't check access
    const hasAccess = forceRender ? false : this.accessAllowed;
    if (hasAccess || !this.template) {
      return Promise.resolve(true);
    }

    return this.render().then(rendered => {
      rendered && this.analytics(Object.assign(
        this.template.additionalAnalytics,
        analyticsData
      ));
      return !rendered;
    });
  }

  /**
   * Display the wall's template and fire the analytics event
   * @returns {Promise<Boolean>} true if rendered
   */
  render() {
    // Will only display the first content element (paragraph)
    document.documentElement.classList.add("keytar-enabled");

    const temp = this.template ? `${this.name} - ${this.type}` : "no template found";
    logger.info(`${debugTag} render: ${temp}`);

    const result = this.template ? this.template.render() : Promise.resolve(false);

    return result.then(rendered => {
      if (!rendered) {
        document.documentElement.classList.remove("keytar-enabled");
      }
      return rendered;
    });
  }

  /**
   * Send the data analytics for this wall
   * @param {Object} shown - of extra analytics data
   * @returns {Object} of sent data
   */
  analytics(shown) {
    const data = Object.assign({
      meterCount: undefined,
      meterLimit: undefined,
      adsVisible: false,
      countVisibleParagraphs: 0,
      viewType: "modal"
    }, shown);
    if (this.template) {
      this.wallAnalyticsSchema.sendData(data);
    }
    return data;
  }

  /**
   * Set a pattern to use for later to check if a given url should be disabled
   * @param {RegExp|Array<RegExp>} pattern - of the regular expressions
   */
  set disableUrls(pattern) {
    const disableUrl = p => {
      if (p instanceof RegExp) {
        this._disabledUrlPatterns.push(p);
      }
    };
    if (Array.isArray(pattern)) {
      pattern.forEach(disableUrl);
    } else {
      disableUrl(pattern);
    }
  }

  /**
   * Check if the wall is disabled for the passed url
   * @param {String} url - location to test
   * @returns {Boolean} true when disabled
   */
  disabledForUrl(url = window.location.toString()) {
    return this._disabledUrlPatterns.find(pattern => pattern.test(url));
  }

  /**
   * Determine if the wall should be run or not, for the current page.
   * @returns {Boolean} true when wall should run
   */
  shouldRun() {
    if (!this._globalEnabled) {
      const message = getProp(this, "tgam.keytar.stateMessage", "");
      logger.info(`${debugTag} Keytar is disabled globally. ${message}`);
      return false;
    }
    if (!this._instanceEnabled) {
      logger.info(`${debugTag} '${this.name}' is disabled, via flag: ${this.featureFlagName}`);
      return false;
    }
    if (this.disabledForUrl()) {
      logger.info("${debugTag} is disabled for current url:", this._disabledUrlPatterns);
      return false;
    }
    return true;
  }

  /**
   * Get the content type label for the page
   * @readonly
   * @returns {String} of content type
   */
  get contentType() {
    // news below is not a type, per se, but a catch all for "none"/"column"
    const pgtype = getProp(this.tgam, "meta.pagetype", "");
    const subtype = getProp(this.tgam, "meta.pagesubtype", "");
    const types = {
      longread: subtype === "longread",
      video: subtype === "video",
      gallery: subtype === "gallery",
      news: pgtype === "story" && ["longread", "gallery", "interactive"].indexOf(subtype) === -1,
      interactive: subtype === "interactive",
      static: pgtype === "static"
    };
    const contentType = Object.keys(types).find(key => types[key] === true);
    logger.info(`${debugTag} contentType:`, contentType);
    return contentType;
  }

  /**
   * @readonly
   * @returns {Boolean} true when wall is disabled
   */
  get disabled() {
    return this._enabled === false;
  }

  /**
   * Set the enabled state of the wall.
   * @param {Boolean} value - to be set
   */
  set enabled(value) {
    this._enabled = value;
  }

  /**
   * @returns {Boolean} true when wall is enabled
   */
  get enabled() {
    return this._enabled;
  }

  /**
   * @param {Object} config - of tgam
   */
  set config(config) {
    this.tgam = config;
  }

  /**
   * @returns {Object} of tgam
   */
  get config() {
    return this.tgam;
  }
}
